import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import { fetchLocalizedCollection, getLocalized } from '../lib/cms';

export default function SuppliersPage() {
  const [items, setItems] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    // 1. Hent sideredaktionelt indhold fra CMS ("sider" samling)
    fetchLocalizedCollection('sider', 'da').then((siderList) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (siderList || []).find(
        (s) => getSlugVal(s) === 'leverandoerer' || getSlugVal(s) === 'suppliers'
      );
      if (match) setPageData(match);
    });

    // 2. Hent leverandører og partnere fra CMS samlingen "Partnere & Leverandører"
    fetchLocalizedCollection('partnere_leverandoerer', 'da').then((rawItems) => {
      if (!rawItems || !rawItems.length) return;

      // Gruppér efter virksomhedsnavn for at fjerne duplikationer uden logo/med ugyldigt logo,
      // og behold alle unikke – også hvis der kun er 1 af en partner eller leverandør (fx Fredko og PM Service)
      const groups = new Map();
      for (const item of rawItems) {
        const rawName =
          typeof item.navn === 'object' ? getLocalized(item.navn, 'da') : item.navn;
        const key = (rawName || '').trim().toLowerCase();
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      }

      const deduplicated = [];
      for (const [key, list] of groups.entries()) {
        if (list.length === 1) {
          // Hvis der kun er 1 af en partner eller leverandør, behold den altid
          deduplicated.push(list[0]);
        } else {
          // Find den post blandt duplikaterne, der har et reelt logo (undgå -1.webp dubletter uden logo)
          const withValidLogo = list.find((it) => {
            const logo = Array.isArray(it.logo) ? it.logo[0] : it.logo;
            return logo && !logo.includes('-1.webp');
          });
          if (withValidLogo) {
            deduplicated.push(withValidLogo);
          } else {
            list.sort(
              (a, b) =>
                new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
            );
            deduplicated.push(list[0]);
          }
        }
      }

      const sorted = deduplicated.sort(
        (a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99)
      );
      setItems(sorted);
    });
  }, []);

  // Sidetekster fra CMS med gode fallbacks
  const title = pageData?.titel || 'Stærke løsninger kræver stærke leverandører';
  const badge = pageData?.undertitel || 'Partnere & Leverandører';
  const intro =
    pageData?.intro ||
    'Der var engang, hvor maskiner alene stod for kræfterne. I dag ved vi, at de stærkeste resultater skabes i samspillet mellem mennesker, viden og de rette leverandører.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Leverandører – Stærke partnere som SKF, Zani & Dimeco';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'PMB forhandler maskiner og udstyr fra førende producenter som SKF, Zani, Dimeco, MVD, Hidroliksan, Mectool, PA Industries og Rotobloc. Se oversigten her.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <Link to="/om-os" className="text-stone-500 hover:text-[#006368] transition">
            Om os
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Leverandører & Partnere</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Derfor arbejder vi kun med de bedste producenter og leverandører i branchen – dem, der sikrer at kraft, præcision og driftssikkerhed bliver til virkelighed i din hverdag.
            </p>
          </div>
        </div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#006368]/10 blur-3xl pointer-events-none" />
      </section>

      {/* Suppliers & Partners Section */}
      <section className="py-16 sm:py-20 bg-stone-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-12 pb-6 border-b border-stone-200">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Vores netværk af leverandører & partnere
              </h2>
              <p className="mt-2 text-sm text-stone-600 max-w-2xl">
                Se vores internationale leverandører og samarbejdspartnere herunder. Klik dig videre til deres officielle hjemmesider for yderligere information.
              </p>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((s, idx) => {
              const rawLogo = Array.isArray(s.logo) ? s.logo[0] : s.logo;
              const name =
                typeof s.navn === 'object' ? getLocalized(s.navn, 'da') : s.navn;
              const rawDesc =
                typeof s.beskrivelse === 'object'
                  ? getLocalized(s.beskrivelse, 'da')
                  : s.beskrivelse;
              const desc = typeof rawDesc === 'string' ? rawDesc : '';

              // Brug primært CMS logoet hvis det findes; ellers fallback hvis tomt eller broken
              let logoUrl = rawLogo;
              if (!logoUrl) {
                const normalizedName = (name || '').toLowerCase();
                if (normalizedName.includes('skf')) {
                  logoUrl = '/_assets/skf.webp';
                } else if (normalizedName.includes('zani')) {
                  logoUrl = '/_assets/zani.webp';
                } else if (normalizedName.includes('dimeco')) {
                  logoUrl = '/_assets/dimeco.webp';
                } else if (normalizedName.includes('mvd')) {
                  logoUrl = '/_assets/mvd.webp';
                } else if (normalizedName.includes('hidroliksan')) {
                  logoUrl = '/_assets/hidroliksan-1.webp';
                } else if (normalizedName.includes('mectool')) {
                  logoUrl = '/_assets/mectool.webp';
                } else if (normalizedName.includes('pa industries') || normalizedName === 'pa') {
                  logoUrl = '/_assets/pa.webp';
                } else if (normalizedName.includes('rotobloc')) {
                  logoUrl = '/_assets/logo-rotobloc-1.png';
                } else if (normalizedName.includes('fredko')) {
                  logoUrl = '/_assets/fredko-final-1.webp';
                } else if (normalizedName.includes('pm service')) {
                  logoUrl = '/_assets/pm-service-final-1.webp';
                }
              }

              return (
                <div
                  key={s.id || idx}
                  className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-2xs hover:border-[#006368] hover:shadow-lg transition-all duration-200 flex flex-col justify-between p-6 group"
                >
                  <div>
                    <div className="h-32 sm:h-36 w-full flex items-center justify-center p-2.5 sm:p-3 bg-stone-300/80 rounded-lg border border-stone-400/60 mb-5 group-hover:border-[#006368]/60 transition">
                      <CmsImage
                        src={logoUrl}
                        alt={name}
                        className="max-h-full max-w-full object-contain filter group-hover:brightness-105 transition scale-105"
                        width={350}
                      />
                    </div>

                    <h3 className="text-lg font-bold text-stone-900 group-hover:text-[#006368] transition">
                      {name}
                    </h3>
                      <p className="mt-2 text-xs sm:text-sm text-stone-600 leading-relaxed">
                        {desc}
                      </p>
                    </div>

                  {/* Actions / Link */}
                  <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-semibold text-[#006368]">
                    {s.hjemmeside ? (
                      <a
                        href={s.hjemmeside}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline flex items-center gap-1.5 group-hover:translate-x-0.5 transition-transform"
                      >
                        <span>Besøg {name}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                      </a>
                    ) : (
                      <span className="text-stone-500">Autoriseret forhandler</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CMS Indhold & Redaktionel sektion */}
          {pageData?.indhold ? (
            <div className="mt-20 p-8 sm:p-12 rounded-2xl bg-white border border-stone-200 shadow-sm max-w-4xl mx-auto">
              <div
                className="prose prose-stone max-w-none text-stone-700 leading-relaxed prose-headings:text-stone-900 prose-headings:font-bold prose-p:my-3 prose-ul:my-3"
                dangerouslySetInnerHTML={{ __html: pageData.indhold }}
              />
              <div className="mt-8 pt-6 border-t border-stone-200 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="px-6 py-3 rounded-lg bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition shadow-sm"
                >
                  Kontakt os for rådgivning om maskiner
                </Link>
                <Link
                  to="/pmb-partnere"
                  className="px-6 py-3 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold text-sm transition"
                >
                  Læs om vores samarbejdspartnere
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-20 p-8 sm:p-10 rounded-2xl bg-white border border-stone-200 max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-stone-900 mb-4">
                Kvalitet, præcision og erfaring i hver eneste løsning
              </h3>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Hver leverandør her repræsenterer mere end et navn og et logo – de repræsenterer kvalitet, præcision og den erfaring, vi bygger vores løsninger på. Fra presser og coil-linjer til komponenter og specialværktøj skaber vores samarbejde med disse partnere den styrke og driftssikkerhed, som <strong>PMB</strong> er kendt for.
              </p>
              <p className="mt-3 text-stone-700 text-sm sm:text-base leading-relaxed font-semibold">
                Det er sammen med dem, vi kan omsætte mere end 100 års erfaring til løsninger, der holder din produktion kørende – i dag og langt ind i fremtiden.
              </p>
              <div className="mt-6 pt-4 border-t border-stone-200 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="px-6 py-3 rounded-lg bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition"
                >
                  Kontakt os for rådgivning om maskiner
                </Link>
                <Link
                  to="/pmb-partnere"
                  className="px-6 py-3 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold text-sm transition"
                >
                  Læs om vores øvrige partnere
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
